import { map } from "lodash";
import { aiccInstance } from "utils/instance";
import { AiccSandboxAction } from "constants/aiccPromptSandbox";
import {
  AiccSandboxActionRequest,
  AiccSandboxVariablesRequest,
  AiccSandboxRenderRequest,
  AiccSandboxTurnRequest,
} from "types/dto/request/aiccPromptSandbox";
import {
  AiccSandboxActionResponse,
  AiccSandboxRenderResponse,
  AiccSandboxTurnResponse,
  AiccSandboxVariablesResponse,
} from "types/dto/response/aiccPromptSandbox";
import {
  transformAiccSandboxAction,
  transformAiccSandboxRender,
  transformAiccSandboxTurn,
} from "types/transformers/aiccPromptSandbox";
import { transformAiccPromptVariable } from "types/transformers/aiccPrompt";

/**
 * The Testing Sandbox (AICC2-334, backend AICC2-528).
 *
 * 🔴 NOT SWR: each call spends money at the provider, so these are writes in every sense that matters, and the
 * results — like the transcript — live in the screen's own state.
 *
 * A 400 covers several causes at once (a request over the size limits, a refused model) and only the backend knows which; `aiccInstance` already surfaces FastAPI's `detail`, so nothing here
 * rewords it. The turn itself is allowed 45 seconds, so nothing here shortens that either.
 */

export async function runAiccSandboxTurn(languageId: number, payload: AiccSandboxTurnRequest) {
  return aiccInstance
    .post<AiccSandboxTurnResponse>(`/languages/${languageId}/prompt-sandbox/turn`, payload)
    .then((response) => transformAiccSandboxTurn(response));
}

/** The action travels twice on purpose: in the payload, and beside it for the transformer that reads `result`. */
export async function runAiccSandboxAction(
  languageId: number,
  action: AiccSandboxAction,
  payload: Omit<AiccSandboxActionRequest, "action">
) {
  return aiccInstance
    .post<AiccSandboxActionResponse>(`/languages/${languageId}/prompt-sandbox/action`, { ...payload, action })
    .then((response) => transformAiccSandboxAction(action, response));
}

export async function fetchAiccSandboxVariables(languageId: number, params: AiccSandboxVariablesRequest) {
  return aiccInstance
    .get<AiccSandboxVariablesResponse>(`/languages/${languageId}/prompt-sandbox/variables`, { params })
    .then((response) => map(response, transformAiccPromptVariable));
}

export async function renderAiccSandbox(languageId: number, payload: AiccSandboxRenderRequest) {
  return aiccInstance
    .post<AiccSandboxRenderResponse>(`/languages/${languageId}/prompt-sandbox/render`, payload)
    .then((response) => transformAiccSandboxRender(response));
}
